import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Alert,
  Fade,
  Slide,
  Stack
} from '@mui/material';
import {
  Close as CloseIcon,
  Visibility as ViewIcon,
  Cancel as DismissIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
  Notifications as NotificationIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';

// Create alarm notification sound using audio file
const createNotificationSound = () => {
  try {
    const alarmSoundPath = '/alarm.wav';
    console.log('🔊 Alarm sound file path:', alarmSoundPath);
    return {
      playNotification: () => {
        const audio = new Audio(alarmSoundPath);
        audio.volume = 0.7;
        audio.currentTime = 0; // Reset to beginning
        
        console.log('🔊 Playing alarm sound from:', alarmSoundPath);
        
        // Play the alarm sound
        audio.play().catch(error => {
          console.error('Failed to play alarm sound:', error);
        });
      }
    };
  } catch (error) {
    console.error('Failed to create audio:', error);
    return {
      playNotification: () => console.log('🔊 Alarm sound would play here')
    };
  }
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const NotificationPopup = ({ notification, onDismiss, onView }) => {
  const navigate = useNavigate();
  const { actions } = useApp();
  const [open, setOpen] = useState(false);
  const soundRef = useRef(null);
  const soundIntervalRef = useRef(null);
  const autoDismissTimerRef = useRef(null);

  // Initialize sound on component mount
  useEffect(() => {
    soundRef.current = createNotificationSound();
  }, []);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      stopContinuousSound();
    };
  }, []);

  // Function to stop continuous sound
  const stopContinuousSound = () => {
    if (soundIntervalRef.current) {
      clearInterval(soundIntervalRef.current);
      soundIntervalRef.current = null;
    }
    if (autoDismissTimerRef.current) {
      clearTimeout(autoDismissTimerRef.current);
      autoDismissTimerRef.current = null;
    }
  };

  useEffect(() => {
    if (notification) {
      setOpen(true);
      
      // Play notification sound immediately
      if (soundRef.current) {
        soundRef.current.playNotification();
      }
      
      // Start continuous sound every 2 seconds
      soundIntervalRef.current = setInterval(() => {
        if (soundRef.current) {
          soundRef.current.playNotification();
        }
      }, 2000);
      
      // Auto-dismiss after 10 seconds if dismissible
      if (notification.dismissible) {
        autoDismissTimerRef.current = setTimeout(() => {
          handleDismiss();
        }, 10000);
      }
      
      // Cleanup on unmount or when notification changes
      return () => {
        stopContinuousSound();
      };
    }
  }, [notification]);

  const handleDismiss = () => {
    // Stop continuous sound
    stopContinuousSound();
    
    setOpen(false);
    setTimeout(() => {
      onDismiss(notification);
    }, 300);
  };

  const handleView = () => {
    // Stop continuous sound
    stopContinuousSound();
    
    setOpen(false);
    setTimeout(() => {
      onView(notification);
      
      // Navigate to the action URL if provided
      if (notification.actionUrl) {
        navigate(notification.actionUrl);
      }
    }, 300);
  };

  const handleCustomAction = (action) => {
    // Stop continuous sound
    stopContinuousSound();
    
    setOpen(false);
    setTimeout(() => {
      if (action.type === 'ignore') {
        onDismiss(notification);
      } else if (action.type === 'view_order') {
        // Handle view order action
        onView(notification, action);
      } else {
        // Default action handling
        onView(notification, action);
      }
    }, 300);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success':
        return <SuccessIcon sx={{ color: 'success.main' }} />;
      case 'warning':
        return <WarningIcon sx={{ color: 'warning.main' }} />;
      case 'error':
        return <ErrorIcon sx={{ color: 'error.main' }} />;
      case 'info':
      default:
        return <InfoIcon sx={{ color: 'info.main' }} />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'success':
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      case 'info':
      default:
        return 'info';
    }
  };

  if (!notification) return null;

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={notification.dismissible ? handleDismiss : undefined}
      disableEscapeKeyDown={!notification.dismissible}
      aria-describedby="notification-dialog-description"
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          border: theme => `2px solid ${theme.palette[getNotificationColor(notification.type)]?.main}`,
          boxShadow: theme => `0 8px 32px ${theme.palette[getNotificationColor(notification.type)]?.main}33`,
          // Add pulsing animation for non-dismissible notifications
          animation: !notification.dismissible ? 'pulse 2s infinite' : 'none',
          '@keyframes pulse': {
            '0%': {
              boxShadow: theme => `0 8px 32px ${theme.palette[getNotificationColor(notification.type)]?.main}33`
            },
            '50%': {
              boxShadow: theme => `0 8px 32px ${theme.palette[getNotificationColor(notification.type)]?.main}66`
            },
            '100%': {
              boxShadow: theme => `0 8px 32px ${theme.palette[getNotificationColor(notification.type)]?.main}33`
            }
          }
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getNotificationIcon(notification.type)}
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {notification.title}
            {!notification.dismissible && (
              <Typography
                component="span"
                variant="caption"
                sx={{
                  ml: 1,
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  backgroundColor: 'error.main',
                  color: 'error.contrastText',
                  fontSize: '10px',
                  fontWeight: 'bold'
                }}
              >
                ACTION REQUIRED
              </Typography>
            )}
          </Typography>
          {notification.dismissible && (
            <IconButton
              aria-label="close"
              onClick={handleDismiss}
              sx={{
                color: theme => theme.palette.grey[500],
                '&:hover': {
                  color: theme => theme.palette.grey[700]
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert 
          severity={getNotificationColor(notification.type)} 
          sx={{ mb: 2, width: '100%' }}
          icon={false}
        >
          <Typography variant="body1" gutterBottom>
            {notification.message}
          </Typography>
          
          {/* Order-specific details */}
          {notification.type === 'order_notification' && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" gutterBottom>
                Order Details:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Order ID:</Typography>
                  <Typography variant="body2" fontWeight="bold">{notification.orderId}</Typography>
                </Box>
                {notification.customerName && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Customer:</Typography>
                    <Typography variant="body2" fontWeight="bold">{notification.customerName}</Typography>
                  </Box>
                )}
                {notification.orderAmount && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Amount:</Typography>
                    <Typography variant="body2" fontWeight="bold">₹{notification.orderAmount}</Typography>
                  </Box>
                )}
                {notification.deliveryDate && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Delivery Date:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {typeof notification.deliveryDate === 'string' && notification.deliveryDate !== 'Not specified' 
                        ? new Date(notification.deliveryDate).toLocaleDateString('en-IN', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })
                        : notification.deliveryDate
                      }
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
          
          {notification.sender && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              From: {notification.sender}
            </Typography>
          )}
          
          {notification.createdAt && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {new Date(notification.createdAt).toLocaleString()}
            </Typography>
          )}
        </Alert>

        {notification.actionUrl && (
          <Box sx={{ p: 2, backgroundColor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Action Required:</strong> This notification has an associated action that you can view.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
          {/* Custom actions for order notifications */}
          {notification.actions && notification.actions.length > 0 ? (
            notification.actions.map((action, index) => (
              <Button
                key={index}
                onClick={() => handleCustomAction(action)}
                variant={action.type === 'view_order' ? 'contained' : 'outlined'}
                startIcon={
                  action.type === 'ignore' ? <DismissIcon /> : 
                  action.type === 'view_order' ? <ViewIcon /> : null
                }
                sx={{ 
                  flex: 1,
                  minWidth: 120
                }}
                color={action.color || 'primary'}
              >
                {action.label}
              </Button>
            ))
          ) : (
            /* Default actions for regular notifications */
            <>
              {notification.dismissible && (
                <Button
                  onClick={handleDismiss}
                  variant="outlined"
                  startIcon={<DismissIcon />}
                  sx={{ 
                    flex: notification.actionUrl ? 1 : 0,
                    minWidth: 120
                  }}
                >
                  Dismiss
                </Button>
              )}
              
              {notification.actionUrl && (
                <Button
                  onClick={handleView}
                  variant="contained"
                  startIcon={<ViewIcon />}
                  sx={{ 
                    flex: 1,
                    minWidth: 120
                  }}
                >
                  View
                </Button>
              )}
              
              {!notification.actionUrl && !notification.dismissible && (
                <Button
                  onClick={handleDismiss}
                  variant="contained"
                  sx={{ 
                    flex: 1,
                    minWidth: 120
                  }}
                >
                  OK
                </Button>
              )}
            </>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

// Multiple notifications queue component
const NotificationQueue = ({ notifications, onDismiss, onView }) => {
  const [currentNotification, setCurrentNotification] = useState(null);
  const [queue, setQueue] = useState([]);

  useEffect(() => {
    if (notifications && notifications.length > 0) {
      setQueue(notifications);
    }
  }, [notifications]);

  useEffect(() => {
    if (queue.length > 0 && !currentNotification) {
      setCurrentNotification(queue[0]);
    }
  }, [queue, currentNotification]);

  const handleDismiss = (notification) => {
    setCurrentNotification(null);
    setQueue(prev => prev.filter(n => n.id !== notification.id));
    onDismiss(notification);
  };

  const handleView = (notification) => {
    setCurrentNotification(null);
    setQueue(prev => prev.filter(n => n.id !== notification.id));
    onView(notification);
  };

  return (
    <NotificationPopup
      notification={currentNotification}
      onDismiss={handleDismiss}
      onView={handleView}
    />
  );
};

export default NotificationPopup;
export { NotificationQueue }; 